import { FC, useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Popper,
  Paper,
} from '@mui/material';
import dayjs from 'dayjs';
import InputMask from 'react-input-mask';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClickAwayListener from '@mui/material/ClickAwayListener';

export interface MaskedDateInputProps {
  date: string; // ISO format: YYYY-MM-DD
  setDate: (newDate: string) => void;
}

const MaskedDateInput: FC<MaskedDateInputProps> = ({ date, setDate }) => {
  const [display, setDisplay] = useState(dayjs(date).isValid() ? dayjs(date).format('MM/DD/YYYY') : '');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const anchorRef = useRef(null);

  // Memoize isTodayOrFuture to ensure consistent calculation
  const isTodayOrFuture = useMemo(() => {
    const today = dayjs().startOf('day');
    const currentDate = dayjs(date);
    return currentDate.isValid() && (currentDate.isAfter(today) || currentDate.isSame(today, 'day'));
  }, [date]);

  useEffect(() => {
    if (dayjs(date).isValid()) {
      setDisplay(dayjs(date).format('MM/DD/YYYY'));
    }
  }, [date]);

  const handleCalendarChange = (val: Date | null) => {
    if (val) {
      const newISO = dayjs(val).format('YYYY-MM-DD');
      const newDisplay = dayjs(val).format('MM/DD/YYYY');
      setDate(newISO);
      setDisplay(newDisplay);
    }
    setCalendarOpen(false);
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {/* ← Button */}
      <IconButton
        onClick={() => setDate(dayjs(date).subtract(1, 'day').format('YYYY-MM-DD'))}
        sx={{
          backgroundColor: 'rgb(68, 99, 136)',
          color: '#fff',
          borderRadius: '8px',
          width: '40px',
          height: '40px',
          boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
          '&:hover': {
            backgroundColor: 'rgb(50, 75, 105)',
            transform: 'scale(1.05)',
          },
        }}
      >
        <ArrowBackIosNewIcon fontSize="small" />
      </IconButton>

      {/* Masked Input with calendar icon */}
      <Box ref={anchorRef}>
        <InputMask
          mask="99/99/9999"
          maskPlaceholder="MM/DD/YYYY"
          alwaysShowMask
          value={display}
          onChange={(e) => {
            const val = e.target.value;
            setDisplay(val);
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
              const [mm, dd, yyyy] = val.split('/');
              const parsed = dayjs(`${yyyy}-${mm}-${dd}`);
              if (parsed.isValid() && !parsed.isAfter(dayjs().startOf('day'))) {
                setDate(parsed.format('YYYY-MM-DD'));
              }
            }
          }}
        >
          {(inputProps: any) => (
            <TextField
              {...inputProps}
              variant="outlined"
              size="small"
              sx={{
                width: 170,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '0.95rem',
                  backgroundColor: '#fff',
                  color: '#3c5471',
                  fontFamily: 'Gotham',
                  '& fieldset': {
                    border: '1px solid rgba(0,0,0,0.23)',
                  },
                  '&:hover fieldset': {
                    border: '1px solid rgba(0,0,0,0.23)',
                  },
                  '&.Mui-focused fieldset': {
                    border: '1px solid rgba(0,0,0,0.23)',
                  },
                },
                '& input': {
                  textAlign: 'center',
                  fontFamily: 'Gotham',
                  letterSpacing: '1px',
                  fontWeight: 'bold',
                  color: '#3c5471',
                },
              }}
              placeholder="MM/DD/YYYY"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setCalendarOpen(true)} size="small">
                      <CalendarTodayIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </InputMask>
      </Box>

      {/* Calendar Popper */}
      <Popper
        open={calendarOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1500 }}
      >
        <ClickAwayListener onClickAway={() => setCalendarOpen(false)}>
          <Paper elevation={3} sx={{ p: 1, mt: 1 }}>
            <ReactDatePicker
              inline
              selected={dayjs(date).isValid() ? dayjs(date).toDate() : null}
              onChange={handleCalendarChange}
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100}
              maxDate={dayjs().startOf('day').toDate()}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* → Button */}
      <IconButton
        disabled={isTodayOrFuture}
        onClick={() => setDate(dayjs(date).add(1, 'day').format('YYYY-MM-DD'))}
        sx={{
          backgroundColor: isTodayOrFuture ? 'rgba(68, 99, 136, 0.5)' : 'rgb(68, 99, 136)',
          color: isTodayOrFuture ? 'rgba(255, 255, 255, 0.5)' : '#fff',
          borderRadius: '8px',
          width: '40px',
          height: '40px',
          boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
          cursor: isTodayOrFuture ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: isTodayOrFuture ? 'rgba(68, 99, 136, 0.5)' : 'rgb(50, 75, 105)',
            transform: isTodayOrFuture ? 'none' : 'scale(1.05)',
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(68, 99, 136, 0.5)',
            color: 'rgba(255, 255, 255, 0.5)',
            cursor: 'not-allowed',
            boxShadow: 'none',
          },
        }}
      >
        <ArrowForwardIosIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default MaskedDateInput;